import { IkeUnit } from '@ikegps/core-measurement';
import { IkeToken, TimingEventAction, TimingEventType } from '@ikegps/core-api';
import { CollectionModelFieldHeight, type IkeDraw } from '@ikegps/core-shared';
import { FieldActionButton } from '../../../components/button/field.action.button';
import { DistanceInput } from '../../../components/input/distance.input';
import { DistanceInputState } from '../../../components/input/distance.input.state';
import { CSS as DistanceInputCSS } from '../../../components/input/distance.input.style';
import { NotificationService } from '../../../components/notification.toast/notification.service';
import { addStyles } from '../../../components/theme';
import { T } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { CaptureThumbnail } from '../../capture.thumbnail/capture.thumbnail';
import type { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

export const CSS = {
	container: 'c-PMLink',
	inputs: 'c-PMLink--inputs',
	isHovered: 'c-PMLink--hovered',
	isLinked: 'c-PMLink--linked',
	height: 'c-PMLink__Height',
};

export const Styles = {
	[CSS.container]: {
		display: 'flex',
		'justify-content': 'space-between',
	},
	[CSS.inputs]: {
		flex: 1,
		display: 'flex',
		'justify-content': 'space-between',
		[`& .${DistanceInputCSS.container}`]: {
			width: '100%',
		},
	},
	[CSS.isHovered]: {
		'font-weight': 'bold',
	},
	[CSS.isLinked]: {
		cursor: 'pointer',
	},
	[CSS.height]: {
		width: '100%',
	},
};

addStyles(Styles);

function getValue(field: CollectionModelFieldHeight, inputState: DistanceInputState): number {
	if (field == null) {
		return 0;
	}

	const unit = Services.UserSettings.units;
	if (unit === IkeUnit.METER) {
		const meters = inputState.meters.value || 0;
		return meters;
	} else {
		const inches = IkeUnit.joinFeetInch(inputState.feet.value || 0, inputState.inch.value || 0);
		const meters = IkeUnit.convert(inches, IkeUnit.METER, IkeUnit.INCH);
		return meters;
	}
}

function selectDrawable(draw: IkeDraw, drawableId: string) {
	draw.state.setSelected(drawableId, null);
	return false;
}

function hoverDrawable(captureId: string, drawableId: string, hovered: boolean) {
	const draw = CurrentState.CollectionEdit.getIkeDraw(captureId);
	if (draw == null) {
		return;
	}
	if (hovered) {
		draw.state.setHovered(drawableId);
	} else if (draw.state.hoverId === drawableId) {
		draw.state.setHovered(null);
	}
}

export const HeightFieldView: CollectionFieldView<CollectionModelFieldHeight> = {
	viewInline(field: CollectionModelFieldHeight): string {
		return IkeUnit.display(field.value, Services.UserSettings.units);
	},

	hasDetail(field: CollectionModelFieldHeight) {
		return field.provider === CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT && field.getCaptures().length > 0;
	},

	viewDetail(field: CollectionModelFieldHeight): MithrilVNode {
		if (!HeightFieldView.hasDetail(field)) {
			return null;
		}

		const capture = field.getCaptures()[0];
		const title = Services.DateTime.formatDateTime(capture.collectedAt.iso);
		return CaptureThumbnail.view(field.collection, capture, { crosshair: true, title });
	},

	viewEdit(field: CollectionModelFieldHeight, ctrl: CollectionEditCtrl): string | MithrilVNode {
		const currentDraw = CurrentState.CollectionEdit.currentDraw;
		const canLinkTo = currentDraw != null && currentDraw.drawState.hasUserMeasurements();

		if (field.hasValue()) {
			let className = CSS.container;
			const heightInline = HeightFieldView.viewInline(field, ctrl);

			if ((field.isPhotoMeasure() || field.isPhotoMeasureManual()) && currentDraw != null) {
				const stateValue = field.stateValue;
				const drawableId = field.stateValue.drawable;
				// If the current field is hovered
				if (currentDraw.state.hoverId === drawableId) {
					className = className + ' ' + CSS.isHovered;
				}
				// If the current field is part of the currentDrawable allow it to select the drawable
				let onclick = null;
				if (stateValue.captureId === currentDraw.capture.objectId) {
					className = className + ' ' + CSS.isLinked;
					onclick = () => selectDrawable(currentDraw, drawableId);
				}

				// Set the hoverId for the drawable
				const onmouseover = () => hoverDrawable(stateValue.captureId, drawableId, true);
				const onmouseout = () => hoverDrawable(stateValue.captureId, drawableId, false);

				if (Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
					let inputState = ctrl.changeState.getState(field.id) as DistanceInputState;
					if (inputState == null) {
						inputState = new DistanceInputState(field.schema.displayName);
						inputState.onValue(() => {
							if (!DistanceInputState.equal(field.value, inputState.value)) {
								field.Mutator.manualFromInput(getValue(field, inputState));
							}
						});
						inputState.meters.onValue(() => {
							inputState.updateMetric();
							if (!DistanceInputState.equal(field.value, inputState.value) && inputState.meters.value != null) {
								field.Mutator.manualFromInput(getValue(field, inputState));
							}
						});
						inputState.feet.onValue(() => {
							inputState.updateImperial();
							if (!DistanceInputState.equal(field.value, inputState.value) && inputState.feet.value != null) {
								field.Mutator.manualFromInput(getValue(field, inputState));
							}
						});
						inputState.inch.onValue(() => {
							inputState.updateImperial();
							if (!DistanceInputState.equal(field.value, inputState.value) && inputState.inch.value != null) {
								field.Mutator.manualFromInput(getValue(field, inputState));
							}
						});
						ctrl.changeState.addState(field.id, inputState);

						const val = field.value;
						if (val != null) {
							inputState.setValue(val);
						}
					}
					//if one of the input state has focus, it means that we are currently manually updating the value
					//In this case, we do not want to overwrite it with the value calculated from the current drawable position
					//otherwise, we update the inputState with the value calculated from the drawable
					if (
						!(inputState.focused || inputState.meters.focused || inputState.feet.focused || inputState.inch.focused)
					) {
						const val = field.value;
						if (val != null) {
							inputState.setValue(val);
						}
					}

					return m('div', { className: CSS.inputs, onmouseover, onmouseout, onclick }, [
						DistanceInput.view(inputState, Services.UserSettings.units),
					]);
				} else {
					return m('div', { className, onmouseover, onmouseout }, [
						m('span', { className: CSS.height, onclick }, heightInline),
					]);
				}
			}

			return m('div', { className }, heightInline);
		}

		if (!canLinkTo) {
			return this.viewInline(field, ctrl);
		}

		const linkFieldIds = currentDraw.state.linkFieldIds;
		if (linkFieldIds.indexOf(field.id) !== -1) {
			return FieldActionButton.viewLinkHeight({
				linking: true,
				onclick: () => {
					currentDraw.state.setTool(null);
					currentDraw.state.setSelected(field.id, null);
				},
			});
		}

		// Some other field is being linked to
		if (linkFieldIds.length > 0) {
			return this.viewInline(field, ctrl);
		}

		// No fields are being linked show the link start behavior
		return FieldActionButton.viewLinkHeight({
			joinable: ctrl.getJoin(field) != null,
			onclick: () => {
				CurrentState.cancelInteractions();
				const oppositeJoin = ctrl.getOppositeJoin(field);
				if (oppositeJoin != null) {
					currentDraw.state.setLinkFields([field.id, oppositeJoin.field.id]);
					NotificationService.info({ key: T.PHOTOMEASURE_JOIN_START, attrs: oppositeJoin.getTitle() });
					Services.Analytics.trackEvent(
						Services.Analytics.EventCategory.Mutate,
						'measurement:join',
						ctrl.collection.revision,
						1
					);
				} else {
					Services.Analytics.trackTiming(
						TimingEventType.HOA,
						TimingEventAction.ENTER,
						currentDraw.collection.id,
						new Date()
					);
					currentDraw.state.setLinkFields([field.id]);
				}
			},
		});
	},
};
